import React, { useState, useCallback, useRef } from 'react';
import { useT } from '../lib/i18n';
import { v4 as uuidv4 } from 'uuid';
import { computeSession, GameFormat } from '../store';
import { batchParse, ParsedTournament, detectFileType } from '../utils/pokerCraftParser';
import { useParsedTournaments } from '../lib/useParsedTournaments';
import { useAuth } from '../hooks/useAuth';
import { useDB } from '../lib/useDB';
import { useFirstImportDetection } from '../hooks/useFirstImportDetection';
import { useDataSharingConsent } from '../hooks/useDataSharingConsent';
import { useUploadPlayerStats } from '../hooks/useUploadPlayerStats';
import { useTeam } from '../hooks/useTeam';
import { useTeamSync } from '../hooks/useTeamSync';
import { emptyStats, addStats } from '../utils/pokerStats/emptyStats';
import { OptInModal } from './OptInModal';
import { logger } from '../lib/logger';



// ── Per-tournament display helpers ────────────────────────────────────────────

const rowVpip = (r: ParsedTournament) =>
  r.handsPlayed > 0 ? Math.round((r.vpipCount / r.handsPlayed) * 100) : null;
const rowPfr = (r: ParsedTournament) =>
  r.handsPlayed > 0 ? Math.round((r.pfrCount / r.handsPlayed) * 100) : null;

function prizeDisplay(r: ParsedTournament): string {
  if (!r.summaryLoaded) return '';
  if (r.heroPrizeIsTicket) return `🎫 $${r.heroPrizeTicketValue}`;
  if (r.heroPrizeTournamentDollars > 0) return `T$${r.heroPrizeTournamentDollars.toFixed(2)}`;
  if (r.heroPrize > 0) return `$${r.heroPrize.toFixed(2)}`;
  return '$0';
}

function itmBadge(r: ParsedTournament) {
  if (!r.summaryLoaded) return null;
  if (r.heroItm) return <span className="badge-itm" title="In The Money">💰 ITM</span>;
  return <span className="badge-bust" title="No cash">✗</span>;
}

// ── ImportRow extends ParsedTournament with UI state ──────────────────────────

type ImportRow = ParsedTournament & {
  selected: boolean;
  buyInInput: string;      // always editable, pre-filled from summary or heuristic
  prizeInput: string;      // editable only when no summary
  bountiesInput: string;
  positionInput: string;
  entrantsInput: string;
};

interface FileStats {
  total: number;
  histories: number;
  summaries: number;
  matched: number;         // have both history + summary
  historyOnly: number;
  summaryOnly: number;
  formats: Record<string, number>;
}

interface Props {
  onViewStats: () => void;
}

export function ImportView({ onViewStats }: Props) {
  const { t } = useT();
  const { user } = useAuth();
  const { importSessions } = useDB(user);
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const { tournaments: allParsed, setTournaments: setAllParsed } = useParsedTournaments();
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [importDone, setImportDone] = useState(0);
  const [fileStats, setFileStats] = useState<FileStats | null>(null);

  // Sort state
  const [sortKey, setSortKey] = useState<string>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Opt-in modal state — shown after the FIRST successful import only
  const { checkIsFirstImport, markImportComplete } = useFirstImportDetection(user);
  const { consent } = useDataSharingConsent(user);
  const { uploadStats } = useUploadPlayerStats(user);
  const { teams } = useTeam(user);
  const { syncStats } = useTeamSync(user, teams);
  const [showOptInModal, setShowOptInModal] = useState(false);

  const processFiles = useCallback(async (files: File[]) => {
    setLoading(true);
    setParseError(null);
    try {
      const MAX_FILE_SIZE_MB = 50;
      const oversized = files.filter(f => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
      if (oversized.length > 0) {
        setParseError(`${oversized.length} archivo(s) superan el límite de ${MAX_FILE_SIZE_MB} MB: ${oversized.map(f => f.name).join(', ')}`);
        return;
      }

      const txtFiles = files.filter(f => f.name.endsWith('.txt'));

      if (txtFiles.length === 0) {
        setParseError('No se encontraron archivos .txt. Asegurate de exportar Game History y/o Game Summary desde PokerCraft.');
        return;
      }

      const parsed = await Promise.all(
        txtFiles.map(f => f.text().then(content => ({
          name: f.name,
          content,
          type: detectFileType(content),
        })))
      );

      const histories = parsed.filter(f => f.type === 'history').length;
      const summaries = parsed.filter(f => f.type === 'summary').length;

      // Cede el hilo para que React renderice el spinner antes de que batchParse
      // bloquee el main thread (es síncrono y puede tardar varios segundos en batches grandes)
      await new Promise(resolve => setTimeout(resolve, 50));

      const results = batchParse(parsed.map(({ name, content }) => ({ name, content })));

      if (results.length === 0) {
        setParseError(
          `Se procesaron ${txtFiles.length} archivos pero no se pudo extraer ningún torneo. ` +
          `Verificá que sean archivos exportados desde GGPoker → PokerCraft.`
        );
        logger.warn('ImportView', 'batchParse retornó 0 torneos', { fileCount: txtFiles.length, histories, summaries });
        return;
      }

      // Dedup by tournamentId
      const seen = new Set<string>();
      const unique = results.filter(r => {
        if (seen.has(r.tournamentId)) return false;
        seen.add(r.tournamentId);
        return true;
      });

      unique.sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));

      const importRows: ImportRow[] = unique.map(r => ({
        ...r,
        selected: true,
        buyInInput: r.buyIn.toString(),
        // Pre-fill prize from summary; otherwise blank for manual entry
        prizeInput: r.summaryLoaded
          ? (r.heroPrizeIsTicket ? '' : r.heroPrize > 0 ? r.heroPrize.toString() : '0')
          : '',
        bountiesInput: '',
        positionInput: r.heroFinishPosition != null ? r.heroFinishPosition.toString() : '',
        entrantsInput: r.totalPlayers > 0 ? r.totalPlayers.toString() : '',
      }));

      setRows(importRows);
      setAllParsed(unique);

      const matched = unique.filter(r => r.summaryLoaded && r.historyLoaded).length;
      const historyOnly = unique.filter(r => r.historyLoaded && !r.summaryLoaded).length;
      const summaryOnly  = unique.filter(r => r.summaryLoaded && !r.historyLoaded).length;
      const formatCount: Record<string, number> = {};
      for (const r of unique) { formatCount[r.format] = (formatCount[r.format] || 0) + 1; }

      setFileStats({ total: unique.length, histories, summaries, matched, historyOnly, summaryOnly, formats: formatCount });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error desconocido al procesar archivos.';
      setParseError(`Error al procesar los archivos: ${msg}`);
      logger.error('ImportView', 'Error inesperado en processFiles', err, { fileCount: files.length });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAllParsed]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    processFiles(Array.from(e.dataTransfer.files));
  }, [processFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) processFiles(Array.from(e.target.files));
  };

  const updateRow = (id: string, field: keyof ImportRow, value: any) => {
    setRows(prev => prev.map(r => r.tournamentId === id ? { ...r, [field]: value } : r));
  };

  const toggleAll = (val: boolean) => setRows(prev => prev.map(r => ({ ...r, selected: val })));

  const handleImport = async () => {
    setImporting(true);
    const selected = rows.filter(r => r.selected);

    // Check if this is the FIRST import BEFORE we save anything.
    // We do the check up-front because once we save, the "has previous sessions"
    // query would return true and we'd never detect the first one.
    // The check is fast (localStorage flag in most cases, single Supabase
    // query in the cold-cache case).
    const isFirstImport = await checkIsFirstImport();

    const byDate: Record<string, ImportRow[]> = {};
    const sessionsToImport: any[] = [];
    // Accumulate (tournament, buyIn) pairs for player_stats upload.
    // Uses the effective buy-in the user saw in the form, so if they edited
    // it manually in the preview, that's what gets bucketed.
    const statsUploadInputs: { tournament: ParsedTournament; buyIn: number }[] = [];
    for (const r of selected) {
      if (!byDate[r.date]) byDate[r.date] = [];
      byDate[r.date].push(r);
      const effectiveBuyIn = parseFloat(r.buyInInput) || 0;
      statsUploadInputs.push({ tournament: r, buyIn: effectiveBuyIn });
    }

    for (const [date, dayRows] of Object.entries(byDate)) {
      const tournaments = dayRows.map(r => {
        // Prize: from summary (ticket handled) or manual input
        let prize = parseFloat(r.prizeInput) || 0;
        if (r.summaryLoaded && r.heroPrizeIsTicket) prize = 0;

        return {
          ...r,
          id: uuidv4(),
          name: r.name,
          format: r.format as GameFormat,
          buyIn: parseFloat(r.buyInInput) || 0,
          rebuys: r.rebuys,
          addon: 0,
          prize,
          bountiesWon: parseFloat(r.bountiesInput) || 0,
          finishPosition: parseInt(r.positionInput) || null,
          totalEntrants: parseInt(r.entrantsInput) || null,
          notes: (() => {
            const parts: string[] = [];
            if (r.historyLoaded) {
              const v = rowVpip(r); const p = rowPfr(r);
              if (v != null) parts.push(`${r.handsPlayed} manos, VPIP ${v}%, PFR ${p}%`);
            }
            if (r.summaryLoaded) {
              if (r.heroPrizeIsTicket) parts.push(`ganó ticket $${r.heroPrizeTicketValue}`);
              if (r.heroPrizeTournamentDollars > 0) parts.push(`T$${r.heroPrizeTournamentDollars}`);
              if (r.rebuys > 0) parts.push(`${r.rebuys} re-entries`);
            }
            return `Importado de PokerCraft${parts.length ? ' — ' + parts.join(', ') : ''}`;
          })(),
        };
      });
      const newSession = computeSession({
        id: uuidv4(),
        date,
        type: tournaments.length === 1 ? 'tournament' : 'multi',
        platform: 'GGPoker',
        stakes: '',
        buyIn: tournaments.reduce((a, t) => a + t.buyIn * (1 + (t.rebuys || 0)) + (t.addon || 0), 0),
        cashOut: tournaments.reduce((a, t) => a + t.prize + t.bountiesWon, 0),
        tournaments,
        staking: { isStaked: false, stakerName: '', myPercent: 100 },
        duration: null,
        notes: `Importado de PokerCraft (${tournaments.length} torneo${tournaments.length > 1 ? 's' : ''})`,
        tags: ['pokercraft-import'],
      });
      sessionsToImport.push(newSession);
    }

    if (sessionsToImport.length > 0) {
      try {
        await importSessions(sessionsToImport);
        setImportDone(selected.length);
        setRows([]);
      } catch (err) {
        logger.error('ImportView', 'Error al persistir sesiones importadas', err, { sessionCount: sessionsToImport.length });
        setParseError('Se guardó localmente pero hubo un error al sincronizar con la nube. Intentá de nuevo más tarde.');
      }
    }
    setImportDone(selected.length);
    setRows([]);
    setFileStats(null);
    if (sessionsToImport.length > 0) onViewStats();

    // Mark as completed (sets local flag) and trigger opt-in modal if applicable.
    // Conditions to show: is first import AND user has not yet opted in.
    // We don't show if consent === null (still loading) — better miss the moment
    // than show on stale data. The user can still opt-in from the header dropdown.
    if (sessionsToImport.length > 0) {
      markImportComplete();
      if (isFirstImport && consent === false) {
        setShowOptInModal(true);
      }

      // ─── Fase 6: fire-and-forget upload to player_stats ─────────────────
      // Only if user opted-in. Runs in the background — UI already shows the
      // import result. Errors are logged but don't affect the user flow.
      if (consent === true && statsUploadInputs.length > 0) {
        uploadStats(statsUploadInputs)
          .then(result => {
            if (result.failed > 0) {
              logger.warn('ImportView', `player_stats upload parcial: ${result.succeeded} ok, ${result.failed} fallidos`, {
                succeeded: result.succeeded, failed: result.failed, skipped: result.skipped,
              });
            } else {
              logger.info('ImportView', `player_stats upload: ${result.attempted} subidos`);
            }
          })
          .catch(err => {
            logger.error('ImportView', 'player_stats upload crasheó', err);
          });
      }

      // ─── Team Sync: fire-and-forget upload de stats al dashboard del coach ──
      // Solo si el usuario tiene un team activo con sync_enabled = true.
      // Agrega todos los torneos del batch actual en un HistoryStats y lo envía.
      const syncableTeams = teams.filter(
        c => c.membership.status === 'active' && c.membership.syncEnabled
      );
      if (syncableTeams.length > 0 && statsUploadInputs.length > 0) {
        const aggregate = emptyStats();
        for (const { tournament } of statsUploadInputs) {
          addStats(aggregate, tournament);
        }

        // Calcular rango de fechas del batch
        const dates = statsUploadInputs
          .map(i => i.tournament.date)
          .filter(Boolean)
          .sort();

        syncStats({
          stats:          aggregate,
          handsCount:     aggregate.handsPlayed,
          dateRangeStart: dates[0] ?? null,
          dateRangeEnd:   dates[dates.length - 1] ?? null,
          stakes:         null,
        })
          .then(result => {
            if (result.rateLimited) {
              logger.warn('ImportView', 'Team Sync omitido por rate limit');
            } else if (result.synced) {
              logger.info('ImportView', 'Team Sync completado');
            } else if (result.error) {
              logger.warn('ImportView', `Team Sync error: ${result.error}`);
            }
          })
          .catch(err => logger.error('ImportView', 'Team Sync crasheó', err));
      }
    }
    setImporting(false);
  };

  // ── Sort ──────────────────────────────────────────────────────────────────

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedRows = [...rows].sort((a, b) => {
    let va: any, vb: any;
    switch (sortKey) {
      case 'date': va = `${a.date}${a.time}`; vb = `${b.date}${b.time}`; break;
      case 'name': va = a.name.toLowerCase(); vb = b.name.toLowerCase(); break;
      case 'buyIn': va = parseFloat(a.buyInInput) || 0; vb = parseFloat(b.buyInInput) || 0; break;
      case 'hands': va = a.handsPlayed; vb = b.handsPlayed; break;
      case 'vpip': va = rowVpip(a) ?? -1; vb = rowVpip(b) ?? -1; break;
      case 'prize': va = a.heroPrize; vb = b.heroPrize; break;
      case 'itm': va = a.heroItm ? 1 : 0; vb = b.heroItm ? 1 : 0; break;
      case 'format': va = a.format; vb = b.format; break;
      case 'match': va = (a.summaryLoaded ? 1 : 0) + (a.historyLoaded ? 1 : 0);
        vb = (b.summaryLoaded ? 1 : 0) + (b.historyLoaded ? 1 : 0); break;
      default: va = 0; vb = 0;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortTh = ({ label, col, title }: { label: string; col: string; title?: string }) => (
    <th
      className={`sortable-th ${sortKey === col ? 'sort-active' : ''}`}
      onClick={() => handleSort(col)}
      title={title}
    >
      {label}<span className="sort-arrow">{sortKey === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ·'}</span>
    </th>
  );

  // ── Derived totals ────────────────────────────────────────────────────────

  const selectedCount = rows.filter(r => r.selected).length;
  const totalInvested = rows.filter(r => r.selected)
    .reduce((a, r) => a + (parseFloat(r.buyInInput) || 0) * (1 + (r.rebuys || 0)), 0);

  // ── Match status badge ────────────────────────────────────────────────────

  function matchBadge(r: ImportRow) {
    if (r.summaryLoaded && r.historyLoaded)
      return <span className="match-full" title="History + Summary cargados">✓</span>;
    if (r.historyLoaded)
      return <span className="match-history" title="Solo History — subí el Summary para tener BI, premio y posición exactos">H</span>;
    return <span className="match-summary" title="Solo Summary — estadísticas de manos no disponibles">S</span>;
  }

  return (
    <div className="import-view">
      <div className="import-header">
        <h2 className="form-title">{t('import.title')}</h2>
        <p className="import-subtitle">
          Arrastrá los archivos .txt de <strong>Game History</strong> y/o <strong>Game Summary</strong> exportados desde GGPoker → PokerCraft
        </p>
        {allParsed.length > 0 && rows.length === 0 && (
          <button className="btn btn-primary" onClick={onViewStats}>
            ◈ Ver Stats & IA ({allParsed.length} torneos) →
          </button>
        )}
      </div>

      {importDone > 0 && (
        <div className="import-success">
          ✓ {importDone} torneos importados exitosamente
          <button className="btn-dismiss" onClick={() => setImportDone(0)}>✕</button>
        </div>
      )}

      {parseError && (
        <div className="import-error">
          ⚠ {parseError}
          <button className="btn-dismiss" onClick={() => setParseError(null)}>✕</button>
        </div>
      )}

      {rows.length === 0 && (
        <>
          <div
            className={`dropzone ${dragging ? 'dragging' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".txt"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
            {loading ? (
              <div className="dz-loading">
                <div className="dz-spinner" />
                <span>{t('import.processing')}</span>
              </div>
            ) : (
              <>
                <div className="dz-icon">⬆</div>
                <div className="dz-text">{t('import.dropzone')}</div>
                <div className="dz-sub">o hacé click para seleccionar archivos</div>
                <div className="dz-hint">{t('import.dropzoneHint')}</div>
              </>
            )}
          </div>

          {/* How to export */}
          <div className="import-howto">
            <div className="panel-title">{t('import.howToTitle')}</div>
            <ol className="howto-steps">
              <li>Abrí GGPoker → <strong>PokerCraft</strong></li>
              <li>Andá a <strong>History</strong> → elegí el rango de fechas</li>
              <li>
                Exportá <strong>Game Summary</strong> (tiene buy-in exacto, premio, posición) y
                también <strong>Game History</strong> (tiene estadísticas de manos)
              </li>
              <li>Arrastrá <strong>todos</strong> los .txt juntos acá arriba — los archivos de un mismo torneo se matchean automáticamente por ID</li>
            </ol>
            <div className="howto-tip">
              <strong>💡 Tip:</strong> Podés subir solo Game Summary (para resultados), solo Game History (para stats), o ambos juntos para tener todo.
            </div>
          </div>
        </>
      )}

      {fileStats && rows.length > 0 && (
        <>
          {/* File summary bar */}
          <div className="import-summary">
            <div className="is-stat">
              <span>Torneos</span>
              <span>{fileStats.total}</span>
            </div>
            <div className="is-stat" title="Tienen History + Summary">
              <span>✓ Completos</span>
              <span className="pos">{fileStats.matched}</span>
            </div>
            {fileStats.historyOnly > 0 && (
              <div className="is-stat" title="Solo Game History">
                <span>H Solo-history</span>
                <span>{fileStats.historyOnly}</span>
              </div>
            )}
            {fileStats.summaryOnly > 0 && (
              <div className="is-stat" title="Solo Game Summary">
                <span>S Solo-summary</span>
                <span>{fileStats.summaryOnly}</span>
              </div>
            )}
            <div className="is-stat">
              <span>ITM</span>
              <span className="pos">{rows.filter(r => r.heroItm).length}</span>
            </div>
            <div className="is-stat">
              <span>Buy-In total</span>
              <span>${rows.filter(r => r.selected).reduce((a, r) => a + (parseFloat(r.buyInInput) || 0) * (1 + (r.rebuys || 0)), 0).toFixed(2)}</span>
            </div>
            {Object.entries(fileStats.formats).map(([fmt, count]) => (
              <div key={fmt} className="is-stat">
                <span>{fmt}</span><span>{count}</span>
              </div>
            ))}
            <div className="is-spacer" />
            {/* VPIP/PFR averages over rows that have history */}
            {(() => {
              const withHistory = rows.filter(r => r.selected && r.historyLoaded && r.handsPlayed > 0);
              if (withHistory.length === 0) return null;
              const avgV = Math.round(withHistory.reduce((a, r) => a + (rowVpip(r) ?? 0), 0) / withHistory.length);
              const avgP = Math.round(withHistory.reduce((a, r) => a + (rowPfr(r) ?? 0), 0) / withHistory.length);
              return (
                <div className="import-bulk-stats">
                  <span className="bulk-stat">VPIP <strong>{avgV}%</strong></span>
                  <span className="bulk-stat">PFR <strong>{avgP}%</strong></span>
                </div>
              );
            })()}
          </div>

          {/* Controls */}
          <div className="import-controls">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={rows.every(r => r.selected)}
                onChange={e => toggleAll(e.target.checked)}
              />
              <span>Seleccionar todos ({selectedCount}/{rows.length})</span>
            </label>
            <button className="btn btn-secondary" onClick={() => { setRows([]); setFileStats(null); }}>
              ✕ Limpiar
            </button>
            <button
              className="btn btn-primary"
              disabled={selectedCount === 0 || importing}
              onClick={handleImport}
            >
              {importing ? t('import.importSaving') : t('import.importAndViewBtn', { n: selectedCount })}
            </button>
          </div>

          {/* Table */}
          <div className="import-table-wrap">
            <table className="import-table">
              <thead>
                <tr>
                  <th></th>
                  <SortTh label="Match" col="match" title="✓ = History + Summary  |  H = solo History  |  S = solo Summary" />
                  <SortTh label="Fecha"   col="date" />
                  <SortTh label="Torneo"  col="name" />
                  <SortTh label="Formato" col="format" />
                  <SortTh label="Buy-In"  col="buyIn" />
                  <SortTh label="Manos"   col="hands" />
                  <SortTh label="VPIP"    col="vpip" />
                  <th>PFR</th>
                  <SortTh label="ITM"     col="itm" />
                  <SortTh label="Prize"   col="prize" />
                  <th>Bounties $</th>
                  <th>Pos.</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => {
                  const vpip = rowVpip(r);
                  const pfr  = rowPfr(r);
                  const buyInChanged = parseFloat(r.buyInInput) !== r.buyIn && r.summaryLoaded;
                  return (
                    <tr key={r.tournamentId} className={r.selected ? '' : 'row-deselected'}>
                      {/* Checkbox */}
                      <td>
                        <input
                          type="checkbox"
                          checked={r.selected}
                          onChange={e => updateRow(r.tournamentId, 'selected', e.target.checked)}
                          className="import-check"
                        />
                      </td>

                      {/* Match status */}
                      <td className="it-center">{matchBadge(r)}</td>

                      {/* Date */}
                      <td className="it-date">{r.date} {r.time}</td>

                      {/* Name */}
                      <td className="it-name" title={r.rawName}>{r.name || r.rawName}</td>

                      {/* Format */}
                      <td>
                        <span className={`fmt-badge fmt-${r.format.toLowerCase()}`}>{r.format}</span>
                      </td>

                      {/* Buy-In — always editable, pre-filled from summary or heuristic */}
                      <td>
                        <div className="import-field-col">
                          <input
                            type="number"
                            className={`import-input import-input-override ${!r.summaryLoaded && r.buyInUnknown && !r.buyInInput ? 'input-warn' : ''
                              }`}
                            value={r.buyInInput}
                            onChange={e => updateRow(r.tournamentId, 'buyInInput', e.target.value)}
                            title={r.summaryLoaded
                              ? 'Buy-in del Summary. Editalo si entraste por satélite (el costo ya está en ese torneo).'
                              : r.buyInUnknown
                                ? 'Buy-in no detectado — ingresalo manualmente'
                                : 'Buy-in detectado del nombre del archivo'
                            }
                          />
                          {buyInChanged && (
                            <span className="field-orig">orig: ${r.buyIn}</span>
                          )}
                          {r.summaryLoaded && !buyInChanged && r.rake > 0 && (
                            <span className="field-note" title={`Rake: $${r.rake}${r.bountyComponent > 0 ? ` + Bounty: $${r.bountyComponent}` : ''}`}>
                              rake ${r.rake}
                            </span>
                          )}
                          {!r.summaryLoaded && r.prizeTicket > 0 && (
                            <span className="field-note">ticket: ${r.prizeTicket}</span>
                          )}
                          {r.rebuys > 0 && (
                            <span className="field-note" title={`${r.rebuys} re-entries × $${r.buyIn} = $${(r.buyIn * (1 + r.rebuys)).toFixed(2)} total`}>
                              +{r.rebuys} re-entry{r.rebuys > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Hands */}
                      <td className="it-mono">{r.historyLoaded ? r.handsPlayed : '—'}</td>

                      {/* VPIP */}
                      <td className="it-mono">{vpip != null ? `${vpip}%` : '—'}</td>

                      {/* PFR */}
                      <td className="it-mono">{pfr != null ? `${pfr}%` : '—'}</td>

                      {/* ITM */}
                      <td className="it-center">
                        {r.summaryLoaded
                          ? itmBadge(r)
                          : <span className="text-muted" title="Cargá el Summary para saber">?</span>
                        }
                      </td>

                      {/* Prize — read-only when summary loaded (shows actual prize), manual otherwise */}
                      <td>
                        {r.summaryLoaded ? (
                          <div className="import-field-col">
                            <span className={`prize-display ${r.heroItm ? 'prize-itm' : 'prize-zero'}`}>
                              {prizeDisplay(r)}
                            </span>
                            {/* Allow bounty manual override even with summary */}
                          </div>
                        ) : (
                          <input
                            type="number"
                            className="import-input"
                            placeholder="0"
                            value={r.prizeInput}
                            onChange={e => updateRow(r.tournamentId, 'prizeInput', e.target.value)}
                          />
                        )}
                      </td>

                      {/* Bounties — always manual (not in summary) */}
                      <td>
                        <input
                          type="number"
                          className="import-input"
                          placeholder="0"
                          value={r.bountiesInput}
                          onChange={e => updateRow(r.tournamentId, 'bountiesInput', e.target.value)}
                        />
                      </td>

                      {/* Position — read-only when summary loaded */}
                      <td>
                        {r.summaryLoaded && r.heroFinishPosition != null ? (
                          <span className="it-mono pos-display">
                            {r.heroFinishPosition}
                            {r.totalPlayers > 0 && <span className="pos-total">/{r.totalPlayers}</span>}
                          </span>
                        ) : (
                          <input
                            type="number"
                            className="import-input import-input-sm"
                            placeholder="—"
                            value={r.positionInput}
                            onChange={e => updateRow(r.tournamentId, 'positionInput', e.target.value)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="import-footer">
            <div className="if-summary">
              {selectedCount} torneos · ${totalInvested.toFixed(2)} invertidos
            </div>
            <button
              className="btn btn-primary btn-save"
              disabled={selectedCount === 0 || importing}
              onClick={handleImport}
            >
              {importing ? t('import.importSaving') : t('import.importAndViewBtn', { n: selectedCount })}
            </button>
          </div>
        </>
      )}

      {/* Opt-in modal — shown only after FIRST successful import, not on every import */}
      <OptInModal
        user={user}
        isOpen={showOptInModal}
        onClose={() => setShowOptInModal(false)}
      />
    </div>
  );
}
